"use client"

import Link from "next/link"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { userLoginV1 } from "@/lib/client/api/login"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"

import { AuthStepProps } from "../types"

// Phone number validation schema
const phoneFormSchema = z.object({
  phoneNumber: z
    .string()
    .min(10, { message: "Số điện thoại phải có ít nhất 10 chữ số" })
    .max(11, { message: "Số điện thoại không được quá 11 chữ số" })
    .regex(/^[0-9]+$/, { message: "Số điện thoại chỉ được chứa chữ số" }),
})

export function PhoneStep({
  onSuccess,
  onPhoneChange,
  startResendCooldown,
}: AuthStepProps) {
  // Phone form
  const form = useForm<z.infer<typeof phoneFormSchema>>({
    resolver: zodResolver(phoneFormSchema),
    defaultValues: {
      phoneNumber: "",
    },
  })

  const isSubmitting = form.formState.isSubmitting

  // Handle phone number submission
  async function handlePhoneSubmit(values: z.infer<typeof phoneFormSchema>) {
    try {
      // Format phone number
      const formattedPhone = values.phoneNumber.replace(/\D/g, "")
      const phoneWithPrefix = formattedPhone.startsWith("0")
        ? `+84${formattedPhone.substring(1)}`
        : `+84${formattedPhone}`

      // Save phone number for later steps
      onPhoneChange?.(phoneWithPrefix)

      // Call the login API
      const response = await userLoginV1(phoneWithPrefix)

      if (response.code === 143) {
        // Determine next step based on login_type
        if (response.login_type === 2) {
          // PIN verification required
          onSuccess("PIN", response)
        } else if (response.login_type === 1) {
          // OTP verification required
          onSuccess("OTP", response)
          startResendCooldown()
        } else if (response.access_token) {
          onSuccess("SUCCESS", response)
        }
      } else {
        // Handle error from API
        toast.error(
          response.msg || "Đăng nhập không thành công. Vui lòng thử lại."
        )
      }
    } catch (err) {
      toast.error("Có lỗi xảy ra. Vui lòng thử lại sau.")
      console.error(err)
    }
  }

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handlePhoneSubmit)}
        className="space-y-6 p-6 pt-2"
      >
        <div className="flex flex-col gap-3">
          <FormField
            control={form.control}
            name="phoneNumber"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center justify-center border border-gray-20/60 rounded-xl h-15 px-4">
                      🇻🇳
                      <span className="ml-2 text-gray-700 text-base md:text-base">
                        +84
                      </span>
                    </div>
                    <Input
                      placeholder="Nhập số điện thoại của bạn"
                      className="h-15 rounded-xl text-gray-900 placeholder:text-gray-700 px-5 text-base md:text-base"
                      {...field}
                    />
                  </div>
                </FormControl>
                <FormMessage className="ml-27" />
              </FormItem>
            )}
          />
        </div>

        <div className="text-sm text-gray-600">
          Tôi đồng ý với{" "}
          <Link
            href="#"
            className="text-blue-700 hover:underline font-semibold"
          >
            Điều kiện & Điều khoản
          </Link>{" "}
          và{" "}
          <Link
            href="#"
            className="text-blue-700 hover:underline font-semibold"
          >
            Hợp đồng vận chuyển
          </Link>{" "}
          của be
        </div>

        <Button
          type="submit"
          variant="be"
          className="w-full text-black font-medium px-5 py-3 h-13 text-base rounded-xl"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Đang xử lý..." : "Tiếp theo"}
        </Button>
      </form>
    </Form>
  )
}
